//----------------------------------------------------------------------------------------------------------------------
// Sound Property Handler
//----------------------------------------------------------------------------------------------------------------------

import { collectPrefixedProperties } from '../utils/metadata.ts';
import { SAGELogger } from '../utils/logger.ts';
import type { LevelManager } from '../managers/level.ts';

//----------------------------------------------------------------------------------------------------------------------

const logger = new SAGELogger('SoundHandler');

//----------------------------------------------------------------------------------------------------------------------

/**
 * Register the sound property handler.
 *
 * Supports the following properties on scene nodes:
 * - `sound` (string): Path to the sound file
 * - `sound_volume` (number): Volume 0-1, default 1
 * - `sound_loop` (boolean): Whether to loop, default true
 * - `sound_spatial` (boolean): Whether to use 3D spatial audio, default true
 * - `sound_distance` (number): Maximum distance for spatial audio, default 100
 * - `sound_autoplay` (boolean): Whether to autoplay, default true
 * - `sound_channel` (string): Audio channel name, default 'ambient'
 *
 * @param levelManager
 */
export function registerSoundHandler(levelManager : LevelManager) : void
{
    levelManager.registerPropertyHandler('sound', (node, value, level, gameEngine) =>
    {
        const soundPath = value as string;
        const scene = level.scene;

        if(!scene)
        {
            throw new Error('Scene not available for sound handler');
        }

        // Collect prefixed properties
        const extras = collectPrefixedProperties(
            node.metadata as Record<string, unknown>,
            'sound'
        );

        const volume = (extras.volume as number) ?? 1;
        const loop = (extras.loop as boolean) ?? true;
        const spatial = (extras.spatial as boolean) ?? true;
        const maxDistance = (extras.distance as number) ?? 100;
        const autoplay = (extras.autoplay as boolean) ?? true;
        const channel = (extras.channel as string) ?? 'ambient';

        const audioManager = gameEngine.managers.audioManager;

        if(!audioManager)
        {
            logger.warn(`No AudioManager available. Sound for "${ node.name }" skipped.`);
            return;
        }

        audioManager.createSound(
            `${ node.name }_sound`,
            soundPath,
            channel,
            {
                loop,
                autoplay,
                volume,
                spatialEnabled: spatial,
                spatialMaxDistance: maxDistance,
                spatialDistanceModel: 'linear',
            }
        )
            .then((sound) =>
            {
                if(spatial)
                {
                    sound.spatial.attach(node);
                }
            })
            .catch((err) =>
            {
                logger.error(`Failed to create sound for node "${ node.name }": ${ err }`);
            });
    });
}

//----------------------------------------------------------------------------------------------------------------------
